import React, { useEffect, useState } from 'react';
import { ValidationActions } from './ValidationActions';
import { AlertTriangleIcon, CheckIcon } from 'lucide-react';
import styled from 'styled-components';
import { formatInferredValue, getValueByFilePath, isNullOrUndefined, } from '../../../shared';
import { useFluidForm, useFormInstance } from './FormContext';
import { equals, groupBy } from 'ramda';
import { FluidFormFields } from './FormFields.component';
import { useFormInstanceDispatch, useFormInstanceSelect } from './FormInstance.service';
import { useStyleConfig } from './EmbeddedStyles';
const ValidationContainer = styled.div.attrs({
    className: 'fluid-validation-container'
}) `
  padding-top: 1.5rem;
  padding-bottom: 1.5rem;
  // No padding on mobile
  @media (min-width: 1024px) {
    padding: 1.5rem;
  }
  @media (max-width: 1023px) {
    padding: 0.75rem;
  }
`;
const ValidationHeader = styled.div.attrs({
    className: 'fluid-validation-header f-bg-gradient f-border-b'
}) `
  padding: 1.5rem;
  display: flex;
  align-items: center;

  @media (max-width: 1023px) {
    padding: 1rem;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
`;
const HeaderIcon = styled.div.attrs({
    className: 'fluid-validation-header-icon'
}) `
  width: 2.5rem;
  height: 2.5rem;
  background: ${props => props.$bgColor || '#fed7aa'};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
`;
const HeaderContent = styled.div.attrs({
    className: 'fluid-validation-header-content'
}) `
  flex: 1;
`;
// Remove old unused styled components - keeping ValidationActionCell for backward compatibility
const ValidationActionCell = styled.div.attrs({
    className: 'fluid-validation-action-cell'
}) `
  min-width: 200px;

  @media (max-width: 1023px) {
    min-width: unset;
  }
`;
const SectionsContainer = styled.div `
  display: flex;
  flex-direction: column;
  gap: 1.5rem;

  @media (max-width: 1023px) {
    gap: 0.5rem;
  }
`;
// Panel components matching ValidationScreen styling
const PanelWrapper = styled.div.attrs({
    className: 'validation-panel-wrapper f-border'
}) `
  border-radius: 0.5rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  margin-bottom: 1.5rem;

  @media (max-width: 1023px) {
    border: none !important;
    border-radius: 0;
    box-shadow: none;
    margin-bottom: 0.75rem;
  }
`;
const PanelContent = styled.div.attrs({
    className: 'validation-panel-content f-bg'
}) `
  @media (max-width: 1023px) {
    padding: 0;
  }
`;
const PanelHeader = styled.div.attrs({
    className: 'validation-panel-header f-bg-gradient f-border-b'
}) `
  border-radius: 0.5rem 0.5rem 0 0;
  padding: 0.75rem 1rem;

  @media (max-width: 1023px) {
    background: none !important;
    border: none !important;
    border-radius: 0;
    padding: 0 0 0.375rem 0;

    h3 {
      font-size: 0.75rem;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      opacity: 0.5;
    }
  }
`;
// Custom table styling to match ValidationScreen
const StyledTable = styled.table.attrs({
    className: 'validation-table'
}) `
  width: 100%;
  border-collapse: collapse;

  @media (max-width: 1023px) {
    display: block;

    thead {
      position: absolute;
      left: -9999px;
      top: -9999px;
    }

    tbody {
      display: block;
    }
  }
`;
const StyledTableHead = styled.thead.attrs({
    className: 'validation-table-head f-bg-dark'
}) `
`;
const StyledTableHeaderCell = styled.th.attrs({
    className: 'validation-table-header-cell f-text-light'
}) `
  padding: 0.75rem 1rem;
  text-align: left;
  font-weight: 500;
  font-size: 0.875rem;
`;
const StyledTableRow = styled.tr.attrs({
    className: 'validation-table-row'
}).withConfig({
    shouldForwardProp: (prop) => !['highlight', '$isError'].includes(prop),
}) `
  ${props => props.$isError && 'background: #fef2f2;'}
  ${props => props.highlight && 'background:rgb(254, 251, 219);'}

  @media (max-width: 1023px) {
    display: grid;
    grid-template-columns: 1fr auto;
    grid-template-rows: auto auto auto;
    column-gap: 0.5rem;
    row-gap: 0.25rem;
    border: 1px solid #e5e7eb;
    border-radius: 0.5rem;
    margin-bottom: 0.5rem;
    padding: 0.625rem 0.75rem;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.04);
    position: relative;

    ${props => props.$isError && `
      border-color: #fecaca;
      background: #fef2f2;
    `}

    ${props => props.highlight && `
      border-color: #fde68a;
      background: rgb(254, 251, 219);
    `}

    /* Row 1: Field label (left) + Actions (right) */
    > td:nth-child(1) {
      grid-column: 1;
      grid-row: 1;
      align-self: center;
    }

    > td:nth-child(4) {
      grid-column: 2;
      grid-row: 1;
      justify-self: end;
      align-self: center;
    }

    /* Row 2: Value spans full width */
    > td:nth-child(2) {
      grid-column: 1 / -1;
      grid-row: 2;
    }

    /* Row 3: Status spans full width */
    > td:nth-child(3) {
      grid-column: 1 / -1;
      grid-row: 3;
    }
  }
`;
const StyledTableCell = styled.td.attrs({
    className: 'validation-table-cell f-text'
}) `
  padding: 0.75rem 1rem;
  font-size: 0.875rem;
  border-bottom: 1px solid ${props => props.$borderColor || '#f3f4f6'};

  @media (max-width: 1023px) {
    display: block;
    padding: 0;
    border-bottom: none;
    font-size: 0.875rem;

    &::before { display: none; }

    /* Field name */
    &:nth-child(1) span {
      font-size: 0.875rem;
    }

    /* Value: muted when not editing */
    &:nth-child(2) {
      color: #6b7280;
      font-size: 0.9375rem;
    }

    /* Status + Actions: no extra padding */
    &:nth-child(3),
    &:nth-child(4) {
      padding: 0;
    }
  }
`;
const ActionButton = styled.button.attrs({
    className: 'validation-action-button'
}) `
  padding: 0.375rem;
  border: none;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 2rem;
  min-height: 2rem;

  ${props => {
    switch (props.$variant) {
        case 'save':
            return `
          background: #dcfce7;
          color: #15803d;
          &:hover { background: #bbf7d0; }
        `;
        case 'cancel':
            return `
          background: #fecaca;
          color: #dc2626;
          &:hover { background: #fca5a5; }
        `;
        case 'edit':
        default:
            return `
          background: transparent;
          color: #3b82f6;
          &:hover {
            background: #dbeafe;
            color: #1d4ed8;
          }
        `;
    }
}}
`;
const ActionsWrapper = styled.div.attrs({
    className: 'validation-actions-wrapper'
}) `
  display: flex;
  align-items: center;
  gap: 0.5rem;

  @media (max-width: 1023px) {
    flex-wrap: wrap;
    justify-content: flex-end;
  }
`;
const InlineEditWrapper = styled.div.attrs({
    className: 'validation-inline-edit'
}) `
  display: flex;
  align-items: center;
  gap: 0.5rem;

  @media (max-width: 1023px) {
    > div:first-child {
      flex: 1;
      min-width: 0;
    }
  }
`;
const HeaderTitle = styled.h2.attrs({
    className: 'f-text'
}) `
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;

  @media (max-width: 1023px) {
    font-size: 1.125rem;
  }
`;
const HeaderSubtitle = styled.p.attrs({
    className: 'f-text-light'
}) `
  margin: 0.25rem 0 0;
  font-size: 1rem;

  @media (max-width: 1023px) {
    font-size: 0.875rem;
  }
`;
const EmptySection = styled.div.attrs({
    className: 'validation-empty-section f-text-light'
}) `
  padding: 1rem;
  font-size: 0.875rem;
  font-style: italic;
  text-align: center;
`;
// Footer styling matching ValidationScreen.tsx
const ValidationFooter = styled.div.attrs({
    className: 'validation-footer f-bg-dark f-border-t'
}) `
  padding: 1.5rem;
  display: flex;
  justify-content: space-between;
  position: sticky;
  bottom: 0;
  box-shadow: 0 -4px 6px -1px rgba(0, 0, 0, 0.1);

  @media (max-width: 1023px) {
    flex-direction: column-reverse;
    gap: 0.75rem;
    padding: 1rem;

    > * {
      width: 100%;
    }

    button {
      width: 100%;
      justify-content: center;
    }
  }
`;
const SecondaryButton = styled.button.attrs({
    className: 'validation-footer-button f-bg'
}) `
  padding: 0.625rem 1.5rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s ease;
  border: 1px solid ${props => props.$borderColor || '#d1d5db'};
  color: inherit;

  &:disabled {
    opacity: 0.75;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    opacity: 0.85;
  }
`;
const LoadingSpinner = styled.svg.attrs({
    className: 'validation-loading-spinner'
}) `
  animation: spin 1s linear infinite;
  margin-left: -0.25rem;
  margin-right: 0.5rem;
  height: 1rem;
  width: 1rem;
  color: white;

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;
const StatusBadge = styled.div.attrs({
    className: 'validation-status-badge'
}) `
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.5rem;
  border-radius: 0.375rem;
  font-size: 0.75rem;
  font-weight: 500;

  ${props => {
    switch (props.$variant) {
        case 'danger':
            return `
          background: #fee2e2;
          color: #b91c1c;
          &::before {
            content: '●';
            color: #dc2626;
          }
        `;
        case 'success':
            return `
          background: #f0fdf4;
          color: #16a34a;
          &::before {
            content: '●';
            color: #16a34a;
          }
        `;
        case 'warning':
            return `
          background: #fffbeb;
          color:rgb(0, 0, 0);
          &::before {
            content: '●';
            color:rgb(0, 0, 0);
          }
        `;
        case 'neutral':
            return `
          background: #f3f4f6;
          color: #6b7280;
          &::before {
            content: '●';
            color: #6b7280;
          }
        `;
    }
}}
`;
const StatusMessage = styled.p.attrs({
    className: 'validation-status-message'
}) `
  margin: 0.25rem 0 0;
  font-size: 0.75rem;
  color: #dc2626;
`;
function ValidationSection({ sectionTitle, fields, fluidForm, editingFields, tempValues, onEditToggle, setTempValues, borderColor }) {
    return (React.createElement(PanelWrapper, null,
        React.createElement(PanelHeader, null,
            React.createElement("h3", { className: "f-text", style: { margin: 0, fontWeight: 500, fontSize: '1rem' } }, sectionTitle)),
        React.createElement(PanelContent, null, fields && fields.length > 0 ? (React.createElement(StyledTable, null,
            React.createElement(StyledTableHead, null,
                React.createElement("tr", null,
                    React.createElement(StyledTableHeaderCell, null, "Field"),
                    React.createElement(StyledTableHeaderCell, null, "Value"),
                    React.createElement(StyledTableHeaderCell, null, "Status"),
                    React.createElement(StyledTableHeaderCell, null, "Actions"))),
            React.createElement("tbody", null, fields.map((field, index) => (React.createElement(ValidationRow, { key: `${sectionTitle}-${index}`, fieldPath: field.fieldPath, validation: fluidForm.getFieldValidationResult(field.fieldPath), configService: fluidForm, isEditing: editingFields[field.fieldPath], tempValue: getValueByFilePath(field.fieldPath, tempValues), onEditToggle: onEditToggle, setTempValues: setTempValues, borderColor: borderColor })))))) : (React.createElement(EmptySection, null, "No fields to validate in this section")))));
}
function ValidationRow({ fieldPath, validation, configService, isEditing, tempValue, onEditToggle, borderColor, }) {
    if (!validation) {
        validation = {
            fieldPath,
            type: "none",
            question: "",
        };
    }
    let userInput = configService.getValue(fieldPath);
    tempValue = isNullOrUndefined(tempValue) ? '' : tempValue;
    userInput = isNullOrUndefined(userInput) ? '' : userInput;
    const hasError = (validation.type === 'retry' || validation.type === 'missing-part');
    const valueChanged = !equals(userInput, tempValue);
    const handleSave = () => {
        // setTempValue(configService.getValue(fieldName));
        onEditToggle(fieldPath);
    };
    return (React.createElement(StyledTableRow, { "$isError": hasError && !valueChanged, highlight: valueChanged },
        React.createElement(StyledTableCell, { "$borderColor": borderColor, "data-label": "Field" },
            React.createElement("span", { className: "f-text", style: { fontWeight: 600 } }, configService.getConfig(fieldPath)?.label || fieldPath)),
        React.createElement(StyledTableCell, { "$borderColor": borderColor, "data-label": "Value" }, isEditing ? (React.createElement(InlineEditWrapper, null,
            React.createElement("div", { style: { width: '100%' } },
                React.createElement(FluidFormFields, { field: configService.getFieldRenderView(fieldPath), options: {
                        omitLabel: true,
                        useStructuredOutput: true,
                        hideInlineValidation: true
                    } })),
            React.createElement(ActionButton, { "$variant": "save", onClick: handleSave, title: "Save" }, "\u2713"))) : (React.createElement("div", { style: { display: 'flex', alignItems: 'center' } },
            React.createElement("span", { style: { color: hasError ? '#dc2626' : undefined } }, formatInferredValue(userInput, configService.getConfig(fieldPath)) || '--')))),
        React.createElement(StyledTableCell, { "$borderColor": borderColor, "data-label": "Status" },
            React.createElement(ValidationActionCell, null,
                valueChanged ? React.createElement(StatusBadge, { "$variant": "warning" }, "Updated") : null,
                !valueChanged ? React.createElement(ValidationActions, { validation: validation, configService: configService, isEditing: isEditing }) : null)),
        React.createElement(StyledTableCell, { "$borderColor": borderColor, "data-label": "Actions" },
            React.createElement(ActionsWrapper, null,
                validation.isAIValidation ? (React.createElement("div", { className: "relative inline-flex group" },
                    React.createElement("span", { className: "inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-[11px] font-semibold text-gray-700" }, "AI"),
                    React.createElement("div", { className: "pointer-events-none absolute z-10 hidden w-64 max-w-[calc(100vw-2rem)] whitespace-normal rounded bg-gray-900 px-2 py-1 text-xs leading-snug text-white shadow-lg group-hover:block right-0 top-full mt-1 lg:right-auto lg:left-1/2 lg:top-0 lg:mt-0 lg:-translate-x-1/2 lg:-translate-y-[calc(100%+8px)]" },
                        "This validation was generated by AI and may be incorrect. Please double-check.",
                        React.createElement("div", { className: "absolute border-4 border-transparent right-2 bottom-full border-b-gray-900 lg:right-auto lg:left-1/2 lg:bottom-auto lg:top-full lg:-translate-x-1/2 lg:border-b-transparent lg:border-t-gray-900" })))) : null,
                !isEditing && (React.createElement(ActionButton, { "$variant": "edit", onClick: () => onEditToggle(fieldPath), title: "Edit value" }, "Edit")),
                validation.isAIValidation && React.createElement(ConfirmValueButton, { fieldPath: fieldPath })))));
}
export function ConfirmValueButton({ fieldPath, text = "Ignore" }) {
    const instanceId = useFormInstance();
    const dispatch = useFormInstanceDispatch(instanceId);
    return (React.createElement("button", { onClick: () => dispatch("confirmField", fieldPath), className: "inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded hover:bg-green-100 hover:border-green-300 transition-colors" },
        React.createElement(CheckIcon, { className: "w-3 h-3" }),
        text));
}
export function FluidValidation({ onSubmitForm, showFooter = true }) {
    const fluidForm = useFluidForm();
    const instanceId = useFormInstance();
    const validations = useFormInstanceSelect((state) => state.fluidForm.getValidationResults());
    const isSubmitting = useFormInstanceSelect((state) => state.isSubmitting);
    const dispatch = useFormInstanceDispatch(instanceId);
    const submitButtonLabel = fluidForm.getAdditionalConfigValue("styleConfig", "submitButtonLabel") || "Submit";
    const styles = useStyleConfig();
    const [editingFields, setEditingFields] = useState({});
    const [tempValues, setTempValues] = useState(fluidForm.getFormSubmission());
    const [counter, setCounter] = useState(0);
    useEffect(() => {
        setTempValues(fluidForm.getFormSubmission());
    }, [validations]);
    const handleSubmit = () => {
        onSubmitForm?.();
    };
    const handleEditToggle = (fieldName) => {
        setCounter(counter + 1);
        setEditingFields(prev => ({
            ...prev,
            [fieldName]: !prev[fieldName]
        }));
    };
    const handleBackToForm = () => {
        dispatch("setSubmissionStage", "draft");
    };
    const allFields = fluidForm.getRenderViewMap();
    const allFieldsBySection = groupBy((field) => fluidForm.getFieldSection(field.fieldPath)?.label, Object.values(allFields).filter(field => fluidForm.isInputField(field)));
    return (React.createElement("div", { className: "f-bg", style: { width: '100%' } },
        React.createElement(ValidationHeader, null,
            React.createElement(HeaderIcon, { "$bgColor": styles.backgroundColorLightHex },
                React.createElement(AlertTriangleIcon, { style: { width: '1.5rem', height: '1.5rem', color: styles.primaryColor } })),
            React.createElement(HeaderContent, null,
                React.createElement(HeaderTitle, null, "Let's review your data together"),
                React.createElement(HeaderSubtitle, null, "We found some issues that need your attention. Please review and correct the information below."))),
        React.createElement(ValidationContainer, null,
            React.createElement(SectionsContainer, null, Object.entries(allFieldsBySection).map(([sectionTitle, fields]) => (React.createElement(ValidationSection, { key: sectionTitle || 'Other', sectionTitle: sectionTitle || 'Other', fields: fields, fluidForm: fluidForm, editingFields: editingFields, tempValues: tempValues, onEditToggle: handleEditToggle, setTempValues: setTempValues, borderColor: styles.backgroundColorDarkHex }))))),
        showFooter && (React.createElement(ValidationFooter, null,
            React.createElement(SecondaryButton, { "$borderColor": styles.backgroundColorDarkHex, onClick: handleBackToForm, disabled: isSubmitting }, "Back to Form"),
            React.createElement("div", { style: { display: 'flex', gap: '1rem' } },
                React.createElement("button", { className: "fluid-button-primary", onClick: handleSubmit, disabled: isSubmitting, style: {
                        gap: '0.5rem',
                        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                        opacity: isSubmitting ? 0.75 : 1,
                        cursor: isSubmitting ? 'not-allowed' : 'pointer'
                    } }, isSubmitting ? (React.createElement(React.Fragment, null,
                    React.createElement(LoadingSpinner, { xmlns: "http://www.w3.org/2000/svg", fill: "none", viewBox: "0 0 24 24" },
                        React.createElement("circle", { style: { opacity: 0.25 }, cx: "12", cy: "12", r: "10", stroke: "currentColor", strokeWidth: "4" }),
                        React.createElement("path", { style: { opacity: 0.75 }, fill: "currentColor", d: "M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" })),
                    "Submitting...")) : (submitButtonLabel)))))));
}
